<template>

  <div class="scope-container">

    <!-- No Scopes -->
    <div
      v-show  = "video.canPlay && !mediaHasApplicableScopes"
      class   = "wizard-container"
    >
      <Wizard
        icon    = "mdi-grid-off"
      >
        <span class="text-h5">No applicable scope found</span>
      </Wizard>
    </div>

    <!-- Loading -->
    <div
      v-show  = "canShowLoading"
      class   = "wizard-container"
    >
      <LoadingWizard>
      </LoadingWizard>
    </div>

    <!-- Scope -->
    <div
      v-show  = "canShowScope"
      class   = "py-4 px-8 scope-output"
    >

      <div 
        class     = "video-container"
        :class    = "{ fill : doesScopeFillPane }"
      >

        <!-- Video -->
        <video
          ref       = "scope"
          muted
          :controls = "false"
        >
        </video>

        <!-- Graticule -->
        <!-- eslint-disable vue/no-v-html -->
        <svg
          v-html      = "graticuleSvg"
          overflow    = "visible"
          :style      = "{ left: -leftMask, top: -topMask }"
        >
        </svg>
        <!-- eslint-enable vue/no-v-html -->

      </div>

      <ScopeBufferBar class="mt-2 buffer-bar">
      </ScopeBufferBar>

    </div>

    <!-- Tools -->
    <v-toolbar
      v-if        = "canShowScope"
      dense
      class       = "flex-grow-0"
      elevation   = "0"
      color       = "rgba(0, 0, 0, 0.3)"
    >

      <v-spacer></v-spacer>

      <ToolbarButton
        v-if      = "false"
        tooltip   = "Save Scope Image"
        @click    = "onSaveScopeImage"
      >
        <v-icon>mdi-image-move</v-icon>
      </ToolbarButton>

      <v-divider 
        vertical 
        class     = "mx-2"
      >
      </v-divider>

      <v-menu
        top
        offset-y
        left
      >

        <template v-slot:activator="{ on, attrs }">
          <v-btn
            class     = "px-4"
            height    = "100%"
            tile
            color     = "transparent"
            elevation = "0"
            v-bind    = "attrs"
            v-on      = "on"
          >
            <span class="mr-1">{{ selectedScopeName }}</span>
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-list
          dense
        >
          <v-list-item-group
            mandatory
            :value  = "scopes.type"
            color   = "primary"
            @change = "onScopeTypeSelected"
          >
            <v-list-item
              v-for   = "type in scopeTypes"
              :key    = "type.type"
              :value  = "type.type"
            >
              <v-list-item-content>
                <v-list-item-title>{{ type.label }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-item-group>        
        </v-list>

      </v-menu>

    </v-toolbar>

  </div>

</template>

<script>

// #region Imports

/* vuex */
import { mapState } from 'vuex';

/* Telestream */
import {
  SAVE_SCOPE_IMAGE,
  START_SCOPES,
  STOP_SCOPES
} from '@/utils/action-types';
import { SET_SCOPES_ELEMENT } from '@/utils/mutation-types';
import ToolbarButton from './components/buttons/ToolbarButton';
import Wizard from './components/Wizard';
import LoadingWizard from './components/wizards/LoadingWizard';
import ScopeBufferBar from './components/ScopeBufferBar';

// #endregion Imports

export default {

  // #region Properties

  name: 'ScopesPane',
  data() {
    return {
      scopeWidth:    undefined,
      scopeHeight:   undefined,
      scopeObserver: undefined,
      scopeTypes:    [
        {
          type:  'LumaScope',
          label: 'Luma Scope'
        },
        {
          type:  'VectorScope',
          label: 'Vector Scope'
        },
        {
          type:  'ComponentParade',
          label: 'RGB Parade'
        }
      ]
    };
  },
  computed: {
    ...mapState([
      'scopes',
      'video'
    ]),
    isSafari() {
      return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    },
    mediaHasApplicableScopes() {
      /*
       * TEMPORARY:
       *
       * The only test that is currently done is to see if the media is audio-only,
       * but once we add other scopes (including scopes applicable to audio), we
       * should expand this test, and perform it in the scopes store, not this
       * component.
       */
      return (
        this.video.properties &&
        (this.video.properties.videoProperties.length > 0)
      );
    },
    selectedScopeName() {
      const scopeType = this.scopeTypes.find(s => s.type === this.scopes.type);
      return scopeType.label;
    },

    /*
     * Note: There is a problem in Safari where the canplay event is never triggered,
     * so, for now, we don't show the "loading" overlay:
     *
     * https://stackoverflow.com/questions/9920297/html5-audio-canplay-event-doesnt-fire-on-safari-mac-desktop 
     */

    canShowLoading() {
      return (
        this.mediaHasApplicableScopes &&
        !this.scopes.canPlay &&
        !this.isSafari
      );
    },
    canShowScope() {
      return (
        this.mediaHasApplicableScopes &&
        this.video.canPlay && 
        (this.scopes.canPlay || this.isSafari)
      );
    },
    doesScopeFillPane() {
      return !this.scopes.graticule || (this.scopes.graticule.displayMode === 0);
    },
    graticuleSvg() {
      if (this.scopeWidth && this.scopeHeight && this.scopes.graticule) {
        const height = this.scopeHeight;
        const width = this.doesScopeFillPane ? this.scopeWidth : this.scopeHeight;
        //const width = this.scopeWidth;

        const svg = this.scopes.graticule.svg
          .replace('$width$', width)
          .replace('$height$', height);

        return svg;
      }

      return undefined;
    },
    leftMask() {
      let left = this.scopes.graticule?.leftMask;

      if (!this.doesScopeFillPane) {
        left += ((this.scopeHeight - this.scopeWidth) / 2);
      }

      return left
    },
    topMask() {
      return this.scopes.graticule?.topMask;
    }
  },
  components: {
    LoadingWizard,
    ScopeBufferBar,
    ToolbarButton,
    Wizard
  },

  // #endregion Properties

  // #region Life-Cycle

  mounted() {
    this.scopeWidth = this.$refs.scope.clientWidth;
    this.scopeHeight = this.$refs.scope.clientHeight;
    this.scopeObserver = new ResizeObserver(this.onScopeResized).observe(this.$refs.scope);

    this.$store.commit(SET_SCOPES_ELEMENT, this.$refs.scope);
    this.$store.dispatch(START_SCOPES, this.scopes.type);
  },

  beforeDestroy() {
    if (this.scopeObserver) {
      this.scopeObserver.unobserve(this.$refs.scope);
    }
  },

  destroyed() {
    this.$store.dispatch(STOP_SCOPES);
  },

  // #endregion Life-Cycle

  // #region Operations

  methods: {

    // #region Events

    onScopeTypeSelected(type) {
      this.$store.dispatch(STOP_SCOPES);
      this.$store.dispatch(START_SCOPES, type);
    },

    onScopeResized() {
      if (!this.$refs.scope) {
        return;
      }

      this.scopeWidth = this.$refs.scope.clientWidth;
      this.scopeHeight = this.$refs.scope.clientHeight;
    },

    onSaveScopeImage() {
      this.$store.dispatch(SAVE_SCOPE_IMAGE);
    }

    // #endregion Events
  }

  // #endregion Operations

}

</script>

<style lang="scss" scoped>

.scope-container {
  display:        flex;
  flex-direction: column;
  height:         100%;

  .wizard-container,
  .scope-output {
    flex:         1;
  }

  .wizard-container {
    display:          flex;
    align-items:      center;
    justify-content:  center;
  }

  .scope-output {
    display:          flex;
    flex-direction:   column;

    .video-container {
      flex:             1;
      position:         relative;
      
      video, svg {
        position:         absolute;
        width:            100%;
        height:           100%;
      }

      &.fill {
        video {
          object-fit:       fill;
        }


      }
    }
  }

  .scope-selector {
    max-width:              200px;
  }
}

</style>